/**
 * THE PERSON, in the words every Person surface uses.
 *
 * A Patient and a clinician are the same row underneath — one `core.person`
 * carrying the name, the phone and the email — and this catalogue is what stops
 * that from being three different vocabularies on three screens. It was: the
 * chart said `patient.email`, the account card said `profile.email` and the
 * clinic's user editor said `users.email`, and because each was translated on
 * its own the Hebrew workspace printed 'דוא"ל' on one and 'אימייל' on the next
 * for the identical field of the identical Person.
 *
 * What belongs here is a fact about a PERSON, or an act over a record of one.
 * A fact only a chart carries — a birth date, a citizenship, a linked chart —
 * stays with `patients`, and a fact only an account carries — a username, a
 * profession, a signature — stays with `controlCenter`. The test for a key is
 * whether both cards would print it, not whether both cards happen to.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const persons = {
  // The two leaves every Person record opens with.
  'person.identity': 'Identity',
  'person.contact': 'Contact',

  'person.fullName': 'Full name',
  'person.phone': 'Phone',
  'person.email': 'Email',

  // A named absence. Never an empty row and never a dash: a field nobody filled
  // in and a field the screen declined to show would otherwise look identical.
  'person.notRecorded': 'Not recorded',

  // The acts over the record. One set, so the chart and the account card are
  // edited by the same three words in the same order.
  'person.edit': 'Edit details',
  'person.save': 'Save details',
  'person.saving': 'Saving…',
  'person.cancel': 'Cancel',
  'person.lastUpdated': 'Last updated',

  'person.discardTitle': 'Unsaved changes',
  'person.discardDetail':
    'This will discard the changes you made here. Nothing already saved is affected.',
  'person.discardChanges': 'Discard changes',
  'person.keepEditing': 'Keep editing',

  // What a lost race SAYS. The typed text is never thrown away by it, which is
  // why the sentence promises that before it asks for anything.
  'person.staleRevision':
    'These details changed elsewhere while you were editing. Your text is kept; reload the saved revision to see the current details before saving.',
  'person.reloadSaved': 'Reload saved revision',
  'person.reloadFailed':
    'The saved revision could not be loaded. Your changes are still here; try again.',
  'person.saveRejected': 'The details were refused as entered. Check the fields and try again.',
  'person.saveFailed': 'The details could not be saved. Try again.',

  // ---- A PERSON'S NAMES -------------------------------------------------
  // The set of name variants every Person carries, the editor over it and the
  // history beside it. One vocabulary for the chart, the account card, the
  // user editor and registration; `person.names.*` is read by all of them.
  'person.names.title': 'Names',
  'person.names.edit': 'Edit names',
  'person.names.saveNames': 'Save names',
  'person.names.loading': 'Loading names…',
  'person.names.loadFailed': 'The names could not be loaded.',
  'person.names.notFound': 'No names are recorded for this person.',
  'person.names.unsavedIntent': 'name changes',

  // The parts a name is made of. `given` is every given name, so a second
  // given name is a second `given` row; a patronymic is its own kind.
  'person.names.kind.family': 'Family name',
  'person.names.kind.given': 'Given name',
  'person.names.kind.patronymic': 'Patronymic',
  'person.names.kind.prefix': 'Prefix',
  'person.names.kind.suffix': 'Suffix',

  // FHIR HumanName.use, named as a clinician would say it.
  'person.names.use': 'Use',
  'person.names.use.unknown': 'Not stated',
  'person.names.use.usual': 'Usual',
  'person.names.use.official': 'Official',
  'person.names.use.temp': 'Temporary',
  'person.names.use.nickname': 'Nickname',
  'person.names.use.anonymous': 'Anonymous',
  'person.names.use.old': 'Former',
  'person.names.use.maiden': 'Maiden name',

  'person.names.source': 'Source',
  'person.names.source.reported': 'As reported',
  'person.names.source.document': 'From a document',
  'person.names.source.passport': 'From a passport',
  'person.names.source.migration': 'Migrated record',
  'person.names.sourceReference': 'Document reference',

  'person.names.order': 'Order',
  'person.names.order.givenFamily': 'Given name first',
  'person.names.order.familyGiven': 'Family name first',
  'person.names.shortStyle': 'Short form',
  'person.names.shortStyle.full': 'Same as full',
  'person.names.shortStyle.initials': 'Initials with family name',
  'person.names.shortStyle.firstGiven': 'First given name with family name',

  'person.names.language': 'Language of the name',
  'person.names.language.unknown': 'Not stated',
  'person.names.international': 'International spelling',
  'person.names.preferred': 'Preferred',
  'person.names.periodStart': 'In use from',
  'person.names.periodEnd': 'In use until',

  // The marks after a name in the read list. Short, because there are several.
  'person.names.primaryMark': 'Shown',
  'person.names.preferredMark': 'Preferred',
  'person.names.internationalMark': 'International',
  'person.names.enteredInErrorMark': 'Entered in error',
  'person.names.periodFrom': 'From {start}',
  'person.names.periodUntil': 'Until {end}',
  'person.names.periodBetween': '{start} to {end}',

  // The editor's parts and acts.
  'person.names.nth': 'Name {n}',
  'person.names.parts': 'Parts of the name',
  'person.names.partKind': 'Kind of part {n}',
  'person.names.addPart': 'Add part',
  'person.names.movePartUp': 'Move part {n} up',
  'person.names.movePartDown': 'Move part {n} down',
  'person.names.removePart': 'Remove part {n}',
  'person.names.addName': 'Add name',
  'person.names.remove': 'Remove this name',
  'person.names.endUse': 'End use',
  'person.names.enteredInError': 'Entered in error',
  'person.names.previewFull': 'Full:',
  'person.names.previewShort': 'Short:',
  'person.names.previewPending': 'Preview follows as you type.',
  'person.names.shownAs': 'Shown as',
  'person.names.shownWith': 'with',

  // What stops a draft before it is sent.
  'person.names.problem.none': 'At least one name is required.',
  'person.names.problem.blankPart': 'Every part of a name needs a value. Remove a part instead of leaving it empty.',
  'person.names.problem.familyTwice': 'A name can carry one family name.',
  'person.names.problem.periodOrder': 'The last day in use cannot come before the first.',
  'person.names.problem.migrationSource':
    'This name was migrated. Once it is changed, say where the spelling comes from.',

  // What the server said.
  'person.names.saveFailed': 'The names could not be saved. Try again.',
  'person.names.invalid': 'The names were refused as entered. Check the fields and try again.',
  'person.names.ambiguous':
    'Two of these names would be shown in the same place. Mark one as preferred, or end the use of one.',
  'person.names.inactive': 'This record is not active, so its names cannot be changed.',
  'person.names.staleRevision':
    'The names changed elsewhere while you were editing. Your text is kept; reload the saved revision before saving.',

  // The reason a change is recorded under.
  'person.names.reasonLegend': 'Reason',
  'person.names.reasonOtherDetail': 'Say why',
  'person.names.reasonHint': 'Choose the closest reason, or write one.',
  'person.names.reasonOwed': 'Say why the names are changing to save them.',
  'person.names.reason.correction': 'Correcting a mistake',
  'person.names.reason.nameChange': 'The name changed',
  'person.names.reason.documentUpdate': 'A document was updated',
  'person.names.reason.preferenceChange': 'The preference changed',
  'person.names.reason.endUse': 'A name is no longer in use',
  'person.names.reason.enteredInError': 'A name was entered in error',
  'person.names.reason.other': 'Other',
  'person.names.event.registration': 'Registered',
  'person.names.event.migration': 'Migrated',

  // The history beside the names.
  'person.history': 'History',
  'person.names.historyLabel': 'History: names',
  'person.names.historyLoading': 'Loading history…',
  'person.names.historyFailed': 'The history could not be loaded.',
  'person.names.historyEmpty': 'No earlier revisions.',
  'person.names.historyMore': 'Earlier revisions',
  'person.names.recordedAt': 'Recorded',
  'person.names.actorSystem': 'System',
  'person.change': 'Change',
  'person.noChanges': 'Nothing has been changed yet.',
  'person.names.formerly': 'formerly',
  'person.names.group.print': 'How the name is printed',
  'person.names.group.meaning': 'What this name is for',
  'person.names.group.sourcePeriod': 'Source and period',
  "person.names.advanced": "Advanced",
  "person.names.optionalReason": "Reason for the change",
  "person.names.optional": "Optional",
  "person.names.addPatronymic": "Add patronymic",
  "person.names.addMiddle": "Add middle name",
  "person.names.national": "National-language spelling",
  "person.names.addOther": "Other variant (advanced)…",
  "person.names.keepOne": "The last spelling cannot be removed; edit or replace it.",
  "person.names.problem.internationalScript": "Use Latin letters for the international spelling.",
  'person.names.updated': 'Names updated',
  "person.names.noReason": "Do not specify a reason",
} as const
